import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState, Loading, SectionTitle, GradeBadge, scoreTone, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Flame, Star, BookCheck, Target, ArrowRight, ClipboardList, Library, Copy, MessageCircleQuestion } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

export const WeeklyChart = ({ data, testid = "weekly-chart" }) => (
  <div className="h-40" data-testid={testid}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={(data || []).map((d) => ({ ...d, day: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" }) }))}>
        <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={12} />
        <Tooltip cursor={{ fill: "#F5F1E6" }} formatter={(v) => [`${v} XP`, "Earned"]} />
        <Bar dataKey="xp" fill="#0F7B7D" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export default function StudentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/student/dashboard").then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const join = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setJoining(true);
    try {
      const { data: r } = await api.post("/classes/join", { code });
      toast.success(`You joined ${r.name}`);
      setCode("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setJoining(false);
    }
  };

  if (!data) return <Loading />;
  const u = data.user;
  const cont = data.recent_lessons.find((l) => !l.completed) || data.recent_lessons[0];
  const due = data.assignments.filter((a) => !a.completed);

  return (
    <div>
      <PageHeader
        eyebrow={`${u.grade_band} · Student`}
        title={`Hi ${u.name.split(" ")[0]}, ready to learn?`}
        subtitle="Pick up where you left off, finish your assignments, or ask Sage for help."
        actions={<Button asChild variant="outline" className="bg-white" data-testid="dashboard-ask-tutor-button"><Link to="/tutor"><MessageCircleQuestion className="h-4 w-4 mr-2" />Ask Sage</Link></Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
        <div className="md:col-span-7 bg-[#0F7B7D] text-white rounded-[18px] p-6 sm:p-8 flex flex-col justify-between min-h-[200px] fade-up" data-testid="student-dashboard-continue-learning">
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-white/80 font-display">Continue learning</div>
            <div className="mt-3 text-2xl sm:text-3xl font-display font-semibold">{cont ? cont.lesson_title : "Start your first lesson"}</div>
            <p className="mt-2 text-white/85 text-sm">{cont ? (cont.completed ? "Completed — review or retake the quiz." : "You're in the middle of this one.") : "Browse courses picked for your grade level."}</p>
          </div>
          <div className="mt-6">
            <Button className="bg-white text-[#071A22] hover:bg-[#F5F1E6] h-11" onClick={() => navigate(cont ? `/lessons/${cont.lesson_id}` : "/catalog")} data-testid="continue-learning-button">
              {cont ? "Continue" : "Browse courses"} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
        <div className="md:col-span-5 grid grid-cols-2 gap-4">
          <StatCard icon={Star} label="XP earned" value={u.xp || 0} tone="amber" testid="student-dashboard-xp" />
          <StatCard icon={Flame} label="Day streak" value={u.streak || 0} tone="coral" testid="student-dashboard-streak" />
          <StatCard icon={BookCheck} label="Lessons done" value={data.lessons_completed} tone="green" testid="student-dashboard-lessons" />
          <StatCard icon={Target} label="Avg. quiz score" value={data.average_score != null ? `${data.average_score}%` : "—"} tone="sky" testid="student-dashboard-avg" />
        </div>

        <div className="md:col-span-7 bg-white border border-border rounded-[14px] p-5">
          <SectionTitle action={<Badge variant="secondary">{due.length} to do</Badge>}>Assignments</SectionTitle>
          {data.assignments.length === 0 ? (
            <EmptyState icon={ClipboardList} title="No assignments yet" text="When your teacher or parent assigns a lesson, it shows up here." testid="assignments-empty" />
          ) : (
            <ul className="divide-y divide-border" data-testid="student-assignments-list">
              {data.assignments.map((a) => (
                <li key={a.assignment_id} className="py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <Link to={`/lessons/${a.lesson_id}`} className="font-medium hover:underline" data-testid="assignment-link">{a.lesson_title}</Link>
                    <div className="text-xs text-muted-foreground">{a.class_name}{a.due_date ? ` · due ${fmtDate(a.due_date)}` : ""}</div>
                  </div>
                  {a.completed ? (
                    <Badge className="bg-[#DDF7E7] text-[#14663A] hover:bg-[#DDF7E7]">Done {a.best_percent != null ? `· ${a.best_percent}%` : ""}</Badge>
                  ) : (
                    <Button size="sm" asChild data-testid="assignment-start-button"><Link to={`/lessons/${a.lesson_id}`}>Start</Link></Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="md:col-span-5 bg-white border border-border rounded-[14px] p-5">
          <SectionTitle>This week</SectionTitle>
          <WeeklyChart data={data.weekly} />
        </div>

        <div className="md:col-span-7 bg-white border border-border rounded-[14px] p-5">
          <SectionTitle>Recent quiz scores</SectionTitle>
          {data.recent_attempts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Take a quiz at the end of any lesson to see your scores here.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="recent-scores-list">
              {data.recent_attempts.slice(0, 6).map((a) => (
                <li key={a.attempt_id} className="py-2.5 flex items-center justify-between gap-3">
                  <Link to={`/lessons/${a.lesson_id}`} className="text-sm truncate hover:underline">{a.lesson_title}</Link>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</span>
                    <span className={`font-display font-semibold ${scoreTone(a.percent)}`}>{a.percent}%</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="md:col-span-5 space-y-4">
          <div className="bg-white border border-border rounded-[14px] p-5">
            <SectionTitle>Join a class</SectionTitle>
            <form onSubmit={join} className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Class code e.g. K7MP2Q" className="h-11 uppercase" data-testid="join-class-input" />
              <Button type="submit" className="h-11" disabled={joining} data-testid="join-class-button">Join</Button>
            </form>
            {data.classes.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {data.classes.map((c) => <Badge key={c.class_id} variant="secondary" data-testid="student-class-chip">{c.name}</Badge>)}
              </div>
            )}
          </div>
          <div className="bg-[#FFF1C7] border border-[#F2B705]/30 rounded-[14px] p-5">
            <div className="text-sm font-semibold">Parent link code</div>
            <p className="text-xs text-[#5B4A10] mt-1">Share this with a parent so they can follow your progress.</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="font-display text-2xl font-semibold tracking-[0.2em]" data-testid="student-link-code">{u.link_code}</span>
              <Button variant="ghost" size="icon" aria-label="Copy code" onClick={() => { navigator.clipboard?.writeText(u.link_code); toast.success("Code copied"); }} data-testid="copy-link-code-button"><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>

        <div className="md:col-span-12">
          <SectionTitle action={<Button variant="link" asChild className="px-0"><Link to="/catalog">See all courses</Link></Button>}>Recommended for {user.grade_band}</SectionTitle>
          {data.recommended.length === 0 ? (
            <EmptyState icon={Library} title="No courses for your grade yet" action={<Button asChild><Link to="/create">Create a lesson with AI</Link></Button>} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.recommended.map((c) => (
                <Link key={c.course_id} to={`/courses/${c.course_id}`} className="bg-white border border-border rounded-[14px] p-5 lift block" data-testid="recommended-course-card">
                  <div className="flex items-center gap-2"><GradeBadge grade={c.grade_band} /><span className="text-xs text-muted-foreground">{c.subject}</span></div>
                  <div className="mt-3 font-display font-semibold text-lg">{c.title}</div>
                  <p className="text-sm text-muted-foreground mt-1">{c.description}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
